import type { ReactNode } from 'react'
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleDashed,
  RotateCcw,
  X,
} from 'lucide-react'

export type QualificationTabId =
  | 'setup'
  | 'required'
  | 'candidates'
  | 'simulation'
  | 'tolerances'
  | 'calibration'

export type EvidenceState = 'missing' | 'current' | 'stale'
export type AnalyticalState = 'insufficient' | 'pass' | 'marginal' | 'fail'
export type QualificationStageState =
  | 'blocked'
  | 'current'
  | 'stale'
  | 'optional'
  | 'warning'
  | 'failed'

export interface QualificationInputs {
  geometryReady: boolean
  lensTargetReady: boolean
  candidateExists: boolean
  candidateInputReady: boolean
  candidateNominalState: AnalyticalState
  traceRequired: boolean
  traceEvidence: EvidenceState
  toleranceRequired: boolean
  toleranceEvidence: EvidenceState
  toleranceSuccessRate?: number | null
  toleranceSummary?: string | null
  calibrationEvidence: EvidenceState
}

export interface QualificationStage {
  id: 'inputs' | 'nominal' | 'robustness' | 'measurement'
  label: string
  state: QualificationStageState
  detail: string
  tab: QualificationTabId
}

export interface QualificationResult {
  stages: QualificationStage[]
  nextAction: {
    label: string
    tab: QualificationTabId
  }
}

export function evidenceForSubject(
  resultPresent: boolean,
  resultFingerprint: string | null,
  subjectFingerprint: string | null,
  modelMatches = true,
): EvidenceState {
  if (!resultPresent || !subjectFingerprint) return 'missing'
  return resultFingerprint === subjectFingerprint && modelMatches ? 'current' : 'stale'
}

function inputStage(inputs: QualificationInputs): QualificationStage {
  if (!inputs.geometryReady) {
    return { id: 'inputs', label: 'Inputs', state: 'blocked', detail: 'Geometry inputs missing', tab: 'setup' }
  }
  if (!inputs.lensTargetReady) {
    return { id: 'inputs', label: 'Inputs', state: 'blocked', detail: 'Lens target unavailable', tab: 'setup' }
  }
  if (!inputs.candidateExists) {
    return { id: 'inputs', label: 'Inputs', state: 'blocked', detail: 'Select a candidate', tab: 'candidates' }
  }
  if (!inputs.candidateInputReady) {
    return { id: 'inputs', label: 'Inputs', state: 'blocked', detail: 'Candidate inputs incomplete', tab: 'candidates' }
  }
  return {
    id: 'inputs',
    label: 'Inputs',
    state: 'current',
    detail: 'Geometry, target, and candidate ready',
    tab: 'candidates',
  }
}

function nominalStage(inputs: QualificationInputs): QualificationStage {
  if (inputs.candidateNominalState === 'insufficient') {
    return { id: 'nominal', label: 'Nominal', state: 'blocked', detail: 'Nominal screen incomplete', tab: 'simulation' }
  }
  if (inputs.candidateNominalState === 'fail') {
    return { id: 'nominal', label: 'Nominal', state: 'failed', detail: 'Nominal screen failed', tab: 'simulation' }
  }
  if (inputs.candidateNominalState === 'marginal') {
    return { id: 'nominal', label: 'Nominal', state: 'warning', detail: 'Nominal screen is borderline', tab: 'simulation' }
  }
  if (inputs.traceEvidence === 'stale') {
    return {
      id: 'nominal',
      label: 'Nominal',
      state: 'stale',
      detail: inputs.traceRequired ? 'Required trace inputs changed' : 'Optional trace inputs changed',
      tab: 'simulation',
    }
  }
  if (inputs.traceRequired && inputs.traceEvidence === 'missing') {
    return { id: 'nominal', label: 'Nominal', state: 'blocked', detail: 'Required trace missing', tab: 'simulation' }
  }
  return {
    id: 'nominal',
    label: 'Nominal',
    state: 'current',
    detail: inputs.traceEvidence === 'current'
      ? 'Nominal screen and trace ready'
      : 'Nominal screen ready; trace optional',
    tab: 'simulation',
  }
}

function robustnessStage(inputs: QualificationInputs): QualificationStage {
  if (inputs.toleranceEvidence === 'current') {
    if (inputs.toleranceSuccessRate === 0) {
      return {
        id: 'robustness',
        label: 'Robustness',
        state: 'failed',
        detail: inputs.toleranceSummary || 'No sampled trial met every enabled check',
        tab: 'tolerances',
      }
    }
    return {
      id: 'robustness',
      label: 'Robustness',
      state: 'current',
      detail: inputs.toleranceSummary || 'Tolerance evidence ready',
      tab: 'tolerances',
    }
  }
  if (inputs.toleranceEvidence === 'stale') {
    return {
      id: 'robustness',
      label: 'Robustness',
      state: 'stale',
      detail: inputs.toleranceRequired ? 'Required tolerance inputs changed' : 'Optional tolerance inputs changed',
      tab: 'tolerances',
    }
  }
  if (inputs.toleranceRequired) {
    return { id: 'robustness', label: 'Robustness', state: 'blocked', detail: 'Required tolerance evidence missing', tab: 'tolerances' }
  }
  return { id: 'robustness', label: 'Robustness', state: 'optional', detail: 'Tolerance analysis not required', tab: 'tolerances' }
}

function measurementStage(evidence: EvidenceState): QualificationStage {
  if (evidence === 'current') {
    return { id: 'measurement', label: 'Setup measurement', state: 'current', detail: 'Active-setup calibration fit ready', tab: 'calibration' }
  }
  if (evidence === 'stale') {
    return { id: 'measurement', label: 'Setup measurement', state: 'stale', detail: 'Active-setup calibration inputs changed', tab: 'calibration' }
  }
  return { id: 'measurement', label: 'Setup measurement', state: 'optional', detail: 'Active-setup calibration optional', tab: 'calibration' }
}

export function deriveQualification(inputs: QualificationInputs): QualificationResult {
  const stages = [
    inputStage(inputs),
    nominalStage(inputs),
    robustnessStage(inputs),
    measurementStage(inputs.calibrationEvidence),
  ]

  if (stages[0].state === 'blocked') {
    return { stages, nextAction: { label: 'Complete inputs', tab: stages[0].tab } }
  }
  if (
    stages[1].state === 'blocked' ||
    (inputs.traceRequired && stages[1].state === 'stale')
  ) {
    return {
      stages,
      nextAction: {
        label: stages[1].state === 'stale' ? 'Refresh nominal evidence' : 'Complete nominal evidence',
        tab: 'simulation',
      },
    }
  }
  if (
    inputs.toleranceRequired &&
    (stages[2].state === 'blocked' || stages[2].state === 'stale')
  ) {
    return {
      stages,
      nextAction: {
        label: stages[2].state === 'stale' ? 'Refresh robustness evidence' : 'Complete robustness evidence',
        tab: 'tolerances',
      },
    }
  }
  return { stages, nextAction: { label: 'Review screening result', tab: 'candidates' } }
}

export function activeQualificationStageId(activeTab: QualificationTabId): QualificationStage['id'] {
  if (activeTab === 'simulation') return 'nominal'
  if (activeTab === 'tolerances') return 'robustness'
  if (activeTab === 'calibration') return 'measurement'
  return 'inputs'
}

const stateLabel: Record<QualificationStageState, string> = {
  blocked: 'Blocked',
  current: 'Current',
  stale: 'Stale',
  optional: 'Optional',
  warning: 'Warning',
  failed: 'Failed',
}

function StageIcon({ state }: { state: QualificationStageState }) {
  let icon: ReactNode = <CircleDashed size={12} />
  if (state === 'current') icon = <Check size={13} />
  if (state === 'stale') icon = <RotateCcw size={12} />
  if (state === 'warning') icon = <AlertTriangle size={12} />
  if (state === 'failed') icon = <X size={13} />
  return <span className={`qualification-stage-icon ${state}`} aria-hidden="true">{icon}</span>
}

export function QualificationRail({
  inputs,
  activeTab,
  onNavigate,
}: {
  inputs: QualificationInputs
  activeTab: QualificationTabId
  onNavigate: (tab: QualificationTabId) => void
}) {
  const qualification = deriveQualification(inputs)
  const activeStageId = activeQualificationStageId(activeTab)
  return (
    <section className="qualification-rail" aria-labelledby="qualification-title">
      <div className="qualification-heading">
        <div>
          <h2 id="qualification-title">Qualification evidence</h2>
          <p>Selected-candidate screening plus an active-setup measurement check</p>
        </div>
        <button className="qualification-next" type="button" onClick={() => onNavigate(qualification.nextAction.tab)}>
          {qualification.nextAction.label}<ArrowRight size={14} aria-hidden="true" />
        </button>
      </div>
      <ol className="qualification-stages" aria-label="Qualification evidence axes">
        {qualification.stages.map((stage) => (
          <li key={stage.id} className={`qualification-stage ${stage.state}`}>
            <button
              type="button"
              onClick={() => onNavigate(stage.tab)}
              aria-current={activeStageId === stage.id ? 'step' : undefined}
            >
              <StageIcon state={stage.state} />
              <span>
                <strong>{stage.label}</strong>
                <small><span className="qualification-state-label">{stateLabel[stage.state]}:</span> {stage.detail}</small>
              </span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  )
}
